import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { FaPaperPlane, FaEnvelope, FaCheckCircle, FaExclamationTriangle, FaHistory, FaSyncAlt } from 'react-icons/fa';

/**
 * AdminEmailPage
 *
 * Test transactional emails (Pro activated/cancelled/payment failed) and
 * inspect the email_log to verify Resend delivery status.
 */

const TEMPLATES = [
  { key: 'activated_stripe', label: 'Pro aktiveret (Stripe)', icon: '🎉' },
  { key: 'activated_manual_campaign', label: 'Pro aktiveret (manuel kampagne)', icon: '🎁' },
  { key: 'activated_manual', label: 'Pro aktiveret (admin manuel)', icon: '⭐' },
  { key: 'cancelled', label: 'Pro opsagt', icon: '⚠️' },
  { key: 'payment_failed', label: 'Betaling fejlet', icon: '🛑' },
];

const STATUS_STYLE = {
  sent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  skipped: 'bg-gray-100 text-gray-700 border-gray-200',
  error: 'bg-red-100 text-red-800 border-red-200',
};

const AdminEmailPage = () => {
  const [template, setTemplate] = useState('activated_stripe');
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [lastResult, setLastResult] = useState(null);

  const auth = { withCredentials: true };

  const fetchLog = async () => {
    setLoadingLog(true);
    try {
      const r = await axios.get(`${API}/admin/email/log`, auth);
      setLog(r.data.entries || []);
    } catch (e) {
      toast.error('Kunne ikke hente log');
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => { fetchLog(); }, []);

  const handleSend = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const body = { template };
      if (to.trim()) body.to = to.trim();
      const r = await axios.post(`${API}/admin/email/test`, body, auth);
      setLastResult(r.data);
      if (r.data.status === 'sent') {
        toast.success(`Mail sendt til ${r.data.to}`);
      } else if (r.data.status === 'skipped') {
        toast.warning(`Skipped: ${r.data.reason || 'unknown'} (mangler RESEND_API_KEY?)`);
      } else {
        toast.error(r.data.error || 'Send fejlede');
      }
      fetchLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send-kald fejlede');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('da-DK', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return iso; }
  };

  const statusCounts = log.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-email-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <FaEnvelope className="text-cyan-500" />
          Email & Mailflow
        </h1>
        <p className="text-gray-600 mt-2">
          Test transactional mails (Resend) og se hvad der er sendt, skipped eller fejlet.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div className="text-3xl font-bold text-emerald-600" data-testid="stat-sent">{statusCounts.sent || 0}</div>
          <div className="text-xs text-gray-500 uppercase mt-1 tracking-wider">Sendt</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div className="text-3xl font-bold text-gray-500" data-testid="stat-skipped">{statusCounts.skipped || 0}</div>
          <div className="text-xs text-gray-500 uppercase mt-1 tracking-wider">Skipped</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <div className="text-3xl font-bold text-red-600" data-testid="stat-error">{statusCounts.error || 0}</div>
          <div className="text-xs text-gray-500 uppercase mt-1 tracking-wider">Fejlet</div>
        </div>
      </div>

      {/* Test sender */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><FaPaperPlane className="text-cyan-500" /> Send test-mail</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Skabelon</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <label
                  key={t.key}
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    template === t.key
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`template-${t.key}`}
                >
                  <input
                    type="radio"
                    name="template"
                    value={t.key}
                    checked={template === t.key}
                    onChange={(e) => setTemplate(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-xl">{t.icon}</span>
                  <span className="text-sm font-medium text-gray-800">{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modtager (valgfri — tom = din egen admin-email)
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="navn@eksempel.dk"
              data-testid="test-email-to"
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <button
            onClick={handleSend}
            disabled={sending}
            data-testid="send-test-btn"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-purple-600 transition-all active:scale-95 disabled:opacity-50"
          >
            <FaPaperPlane />
            {sending ? 'Sender…' : 'Send test-mail'}
          </button>

          {lastResult && (
            <div className={`mt-4 p-4 rounded-lg border ${
              lastResult.status === 'sent' ? 'bg-emerald-50 border-emerald-200' :
              lastResult.status === 'skipped' ? 'bg-gray-50 border-gray-200' :
              'bg-red-50 border-red-200'
            }`} data-testid="last-result">
              <div className="flex items-start gap-3">
                {lastResult.status === 'sent' ? <FaCheckCircle className="text-emerald-500 text-xl mt-0.5" />
                  : lastResult.status === 'skipped' ? <FaExclamationTriangle className="text-gray-400 text-xl mt-0.5" />
                  : <FaExclamationTriangle className="text-red-500 text-xl mt-0.5" />}
                <div className="text-sm">
                  <div className="font-semibold">{lastResult.status === 'sent' ? 'Sendt' : lastResult.status === 'skipped' ? 'Skipped' : 'Fejl'}</div>
                  <div className="text-gray-600 mt-1">
                    Til: <code>{lastResult.to}</code>
                    {lastResult.resend_id && <> · Resend-ID: <code>{lastResult.resend_id}</code></>}
                    {lastResult.reason && <> · {lastResult.reason}</>}
                    {lastResult.error && <> · {lastResult.error}</>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Log */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="font-bold flex items-center gap-2"><FaHistory className="text-gray-500" /> Email-log (seneste 200)</h2>
          <button
            onClick={fetchLog}
            data-testid="refresh-log-btn"
            disabled={loadingLog}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <FaSyncAlt className={loadingLog ? 'animate-spin' : ''} />
            Opdater
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left p-3">Tidspunkt</th>
                <th className="text-left p-3">Skabelon</th>
                <th className="text-left p-3">Til</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Detalje</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 ? (
                <tr><td colSpan="5" className="text-center text-gray-400 p-8">Ingen mails sendt endnu.</td></tr>
              ) : (
                log.map((e, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`log-row-${i}`}>
                    <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(e.sent_at)}</td>
                    <td className="p-3 font-mono text-xs">{e.template}</td>
                    <td className="p-3">{e.to}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_STYLE[e.status] || 'bg-gray-100 text-gray-700'}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-600">
                      {e.resend_id ? <code className="text-gray-500">{e.resend_id}</code> : (e.error || '—')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailPage;
